import type { ValidationError, Warning } from '../types.js';

/**
 * Strict hex string validation.
 * Accepts 0x-prefixed hex strings with even length.
 */
const HEX_PATTERN = /^0x[0-9a-fA-F]*$/;

export function isValidHex(value: string): boolean {
  return HEX_PATTERN.test(value) && value.length % 2 === 0;
}

/**
 * Validate an Ethereum-style hex address (0x-prefixed, 20 bytes = 42 chars).
 */
export function validateEthereumAddress(
  address: string,
  fieldName: string,
): ValidationError | null {
  if (!HEX_PATTERN.test(address)) {
    return { field: fieldName, message: 'Invalid hex format' };
  }
  if (address.length !== 42) {
    return {
      field: fieldName,
      message: `Expected 20-byte address (42 chars with 0x prefix), got ${address.length} chars`,
    };
  }
  return null;
}

/**
 * Validate a hex-encoded hash (0x-prefixed, 32 bytes = 66 chars).
 */
export function validateHash32(
  hash: string,
  fieldName: string,
): ValidationError | null {
  if (!HEX_PATTERN.test(hash)) {
    return { field: fieldName, message: 'Invalid hex format' };
  }
  if (hash.length !== 66) {
    return {
      field: fieldName,
      message: `Expected 32-byte hash (66 chars with 0x prefix), got ${hash.length} chars`,
    };
  }
  return null;
}

/**
 * Validate that a bigint value is non-negative.
 */
export function validateNonNegativeBigInt(
  value: bigint,
  fieldName: string,
): ValidationError | null {
  if (value < 0n) {
    return { field: fieldName, message: 'Value must be non-negative' };
  }
  return null;
}

/**
 * Validate that a bigint value fits within a uint256.
 */
const MAX_UINT256 = (1n << 256n) - 1n;

export function validateUint256(
  value: bigint,
  fieldName: string,
): ValidationError | null {
  if (value < 0n) {
    return { field: fieldName, message: 'Value must be non-negative' };
  }
  if (value > MAX_UINT256) {
    return { field: fieldName, message: 'Value exceeds uint256 maximum' };
  }
  return null;
}

/**
 * Sanitize a string value for safe display.
 * Strips control characters and null bytes that could be used
 * for display attacks (e.g., right-to-left override).
 */
export function sanitizeDisplayString(value: string): string {
  // Remove control characters (U+0000-U+001F, U+007F-U+009F)
  // Remove bidirectional override characters (U+202A-U+202E, U+2066-U+2069)
  // Remove zero-width characters (U+200B-U+200F, U+FEFF)
  return value.replace(
    // eslint-disable-next-line no-control-regex
    /[\x00-\x1F\x7F-\x9F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g,
    '',
  );
}

/**
 * Check if hex data looks like it contains a contract call
 * (has a 4-byte function selector).
 */
export function hasCalldata(data: string): boolean {
  // "0x" alone or empty = no calldata
  // Need at least "0x" + 8 hex chars (4 bytes) for a selector
  return data.length >= 10 && data !== '0x';
}

/**
 * Generate warnings for common transaction patterns that deserve attention.
 */
export function generateCommonWarnings(params: {
  to: string | null;
  value: bigint;
  data: string;
}): Warning[] {
  const warnings: Warning[] = [];

  if (params.to === null) {
    warnings.push({
      message: 'This transaction deploys a new contract',
      severity: 'warning',
    });
  }

  if (params.value > 0n && hasCalldata(params.data)) {
    warnings.push({
      message:
        'This transaction sends native currency AND calls a contract function',
      severity: 'warning',
    });
  }

  return warnings;
}
